/**
 * Custom Hook: useHealthForm
 * Manages form state, validation, and submission
 */

import { useState, useCallback } from 'react';
import { DeclarationData } from '@/lib/formTypes';
import { validateForm, validateField } from '@/lib/formValidation';

const initialFormState: DeclarationData = {
  // Personal Info
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  phone: '',
  email: '',
  occupation: '',

  // Medical Info
  physicalLimitation: false,
  physicalLimitationDetails: '',

  bloodPressure: '',
  bloodPressureManaged: false,

  surgeries: false,
  surgeriesDetails: '',

  heartEvents: false,
  heartEventsDetails: '',

  injuries: false,
  injuriesDetails: '',

  osteoporosis: false,

  arthritis: false,
  arthritisManaged: false,

  medications: false,
  medicationsDetails: '',

  smoking: false,
  smokingDetails: '',

  pregnancy: false,
  pregnancyWeek: '',

  additionalInfo: '',

  // Declaration
  agreedToTerms: false,
  declarationDate: new Date().toISOString().split('T')[0],
  signature: '',
};

export const useHealthForm = () => {
  const [formData, setFormData] = useState<DeclarationData>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  /**
   * Update single field
   */
  const updateField = useCallback((field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Validate single field
   */
  const validateSingleField = useCallback((field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
      return false;
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      return true;
    }
  }, []);

  /**
   * Validate entire form
   */
  const validateEntireForm = useCallback(() => {
    const validation = validateForm(formData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return false;
    }
    setErrors({});
    return true;
  }, [formData]);

  /**
   * Submit form
   */
  const submitForm = useCallback(async (onSubmit: (data: DeclarationData) => Promise<void>) => {
    if (!validateEntireForm()) {
      setSubmitStatus('error');
      setSubmitMessage('אנא בדוק את כל השדות');
      return false;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await onSubmit(formData);
      setSubmitStatus('success');
      setSubmitMessage('הטופס נשלח בהצלחה!');
      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData(initialFormState);
        setSubmitStatus('idle');
      }, 2000);
      return true;
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(
        error instanceof Error ? error.message : 'שגיאה בשליחת הטופס'
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateEntireForm]);

  /**
   * Reset form
   */
  const resetForm = useCallback(() => {
    setFormData(initialFormState);
    setErrors({});
    setSubmitStatus('idle');
    setSubmitMessage('');
  }, []);

  /**
   * Update signature
   */
  const setSignature = useCallback((signatureData: string) => {
    updateField('signature', signatureData);
  }, [updateField]);

  /**
   * Update declaration date
   */
  const setDeclarationDate = useCallback((date: string) => {
    updateField('declarationDate', date);
  }, [updateField]);

  return {
    formData,
    errors,
    isSubmitting,
    submitStatus,
    submitMessage,
    updateField,
    validateSingleField,
    validateEntireForm,
    submitForm,
    resetForm,
    setSignature,
    setDeclarationDate,
  };
};
